/*  src/services/inventory.service.js
    ------------------------------------------------------------
    Every endpoint in InventoryController.cs wrapped with
    async / await, try / catch, identical style to AssetService
    ------------------------------------------------------------
*/
import config from "../config";
import selectedEnvironment from "../env";
import Constants from "../assests/Constants/constants";

const API_URL = `${config[selectedEnvironment].API_URL}`;

/*---------------------------------------------------
  universal helper
----------------------------------------------------*/
const handleResponse = async (response, errorMessage) => {
 return response;
};

/*---------------------------------------------------
  INVENTORY ITEMS
----------------------------------------------------*/
const CreateInventoryItem = async (data, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/CreateInventoryItem`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return await handleResponse(response, "Failed to create inventory item");
  } catch (err) {
    console.error("Error in CreateInventoryItem:", err);
    throw err;
  }
};

const UpdateInventoryItem = async (payload, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/UpdateInventoryItem`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return await handleResponse(response, "Failed to update inventory item");
  } catch (err) {
    console.error("Error in UpdateInventoryItem:", err);
    throw err;
  }
};


const DeleteInventoryItem = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/DeleteInventoryItem`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to delete inventory item");
  } catch (err) {
    console.error("Error in DeleteInventoryItem:", err);
    throw err;
  }
};

/* merge several inventory items into one master item ----------------- */
const MergeInventoryItems = async (caseId, masterItemId, itemIdsToMerge, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/MergeInventoryItems`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        CaseId: caseId,
        MasterItemId: masterItemId,
        ItemIdsToMerge: itemIdsToMerge,
      }),
    });
    return await response.json();
  } catch (err) {
    console.error("Error in MergeInventoryItems:", err);
    throw err;
  }
};

const GetInventoryItemById = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetInventoryItemById`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to fetch inventory item by ID");
  } catch (err) {
    console.error("Error in GetInventoryItemById:", err);
    throw err;
  }
};

const GetInventoryItemsByCaseId = async (caseId, date , authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetInventoryItemsByCaseId`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Date: date ?? null }),
    });
    return response;
  } catch (err) {
    console.error("Error in GetInventoryItemsByCaseId:", err);
    throw err;
  }
};

const GetInventoryItemsWithClientItemByCaseId = async (caseId, date, authToken) => {
  try {
    const response = await fetch(
      `${API_URL}/Inventory/GetInventoryItemsWithClientItemByCaseId`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ CaseId: caseId, Date: date ?? null }),
      }
    );
    return response;
  } catch (err) {
    console.error("Error in GetInventoryItemsWithClientItemByCaseId:", err);
    throw err;
  }
};

const GetInventoryItemsWithClientItemAndMovementsByCaseId = async (
  payload,
  authToken
) => {
  try {
    const { caseId, date } = payload;
    const response = await fetch(
      `${API_URL}/Inventory/GetInventoryItemsWithClientItemAndMovementsByCaseId`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ CaseId: caseId, Date: date ?? null }),
      }
    );
    return await handleResponse(
      response,
      "Failed to fetch inventory items with movements"
    );
  } catch (err) {
    console.error(
      "Error in GetInventoryItemsWithClientItemAndMovementsByCaseId:",
      err
    );
    throw err;
  }
};

/*---------------------------------------------------
  INVENTORY GROUPS
----------------------------------------------------*/
const CreateInventoryGroup = async (data, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/CreateInventoryGroup`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return await handleResponse(response, "Failed to create inventory group");
  } catch (err) {
    console.error("Error in CreateInventoryGroup:", err);
    throw err;
  }
};

const UpdateInventoryGroup = async (data, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/UpdateInventoryGroup`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return await handleResponse(response, "Failed to update inventory group");
  } catch (err) {
    console.error("Error in UpdateInventoryGroup:", err);
    throw err;
  }
};

const DeleteInventoryGroup = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/DeleteInventoryGroup`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to delete inventory group");
  } catch (err) {
    console.error("Error in DeleteInventoryGroup:", err);
    throw err;
  }
};

const GetInventoryGroupById = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetInventoryGroupById`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to fetch inventory group by ID");
  } catch (err) {
    console.error("Error in GetInventoryGroupById:", err);
    throw err;
  }
};

const GetInventoryGroupsByCaseId = async (caseId, authToken) => {
  try {
    const response = await fetch(
      `${API_URL}/Inventory/GetInventoryGroupsByCaseId`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ CaseId: caseId }),
      }
    );
    return await handleResponse(response, "Failed to fetch inventory groups");
  } catch (err) {
    console.error("Error in GetInventoryGroupsByCaseId:", err);
    throw err;
  }
};

/*---------------------------------------------------
  WAREHOUSES
----------------------------------------------------*/
const CreateWarehouse = async (data, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/CreateWarehouse`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return await handleResponse(response, "Failed to create warehouse");
  } catch (err) {
    console.error("Error in CreateWarehouse:", err);
    throw err;
  }
};

const UpdateWarehouse = async (data, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/UpdateWarehouse`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return await handleResponse(response, "Failed to update warehouse");
  } catch (err) {
    console.error("Error in UpdateWarehouse:", err);
    throw err;
  }
};

const DeleteWarehouse = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/DeleteWarehouse`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to delete warehouse");
  } catch (err) {
    console.error("Error in DeleteWarehouse:", err);
    throw err;
  }
};

const GetWarehouseById = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetWarehouseById`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to fetch warehouse by ID");
  } catch (err) {
    console.error("Error in GetWarehouseById:", err);
    throw err;
  }
};

const GetWarehousesByCaseId = async (caseId, token) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetWarehousesByCaseId`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId }),
    });
    return await handleResponse(response, "Failed to fetch warehouses");
  } catch (err) {
    console.error("Error in GetWarehousesByCaseId:", err);
    throw err;
  }
};

/*---------------------------------------------------
  INVENTORY MOVEMENTS
----------------------------------------------------*/
const CreateInventoryMovement = async (formObj, authToken) => {
  try {
    const fd = new FormData();
    Object.entries(formObj).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, v));

    const response = await fetch(`${API_URL}/Inventory/CreateInventoryMovement`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`, // NO Content-Type header for multipart
      },
      body: fd,
    });
    return await handleResponse(response, "Failed to create inventory movement");
  } catch (err) {
    console.error("Error in CreateInventoryMovement:", err);
    throw err;
  }
};

/* create many at once ------------------------------------------------ */
const CreateMultipleInventoryItems = async (itemsArray, authToken) => {
  try {
    const formData = new FormData();
    
    // For each item in the array, append its properties with indexed keys
    itemsArray.forEach((item, index) => {
      formData.append(`[${index}].CaseId`, item.caseId);
      if (item.clientItemId) formData.append(`[${index}].ClientItemId`, item.clientItemId);
      if (item.warehouseId) formData.append(`[${index}].WarehouseId`, item.warehouseId);
      if (item.groupId) formData.append(`[${index}].GroupId`, item.groupId);
      if (item.dateInInventory) formData.append(`[${index}].DateInInventory`, item.dateInInventory);
      if (item.currentQuantity !== undefined && item.currentQuantity !== null) {
        formData.append(`[${index}].CurrentQuantity`, item.currentQuantity);
      }
      
      // ClientItem fields
      if (item.makat) formData.append(`[${index}].Makat`, item.makat);
      if (item.name) formData.append(`[${index}].Name`, item.name);
      if (item.price !== undefined && item.price !== null) formData.append(`[${index}].Price`, item.price);
      if (item.includeVat !== undefined && item.includeVat !== null) {
        formData.append(`[${index}].IncludeVat`, item.includeVat);
      }
      
      // Image file
      if (item.imageFile) {
        formData.append(`[${index}].ImageFile`, item.imageFile);
      }
    });

    const response = await fetch(
      `${API_URL}/Inventory/CreateMultipleInventoryItems`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData,
      }
    );
    return await handleResponse(
      response,
      "Failed to create multiple inventory items"
    );
  } catch (err) {
    console.error("Error in AddMultipleItems:", err);
    throw err;
  }
};

// InventoryService.js
export const UpdateInventoryMovement = async (dto, token) => {
  
  const resp = await fetch(`${API_URL}/Inventory/UpdateInventoryMovement`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(dto),
  });
  return handleResponse(resp, 'Failed to update inventory movement');
};


const UpdateInventoryItemWithClientItem = async (data, authToken) => {
  try {
    const formData = new FormData();
    
    // Append all fields to FormData
    formData.append('Id', data.id);
    formData.append('CaseId', data.caseId);
    if (data.clientItemId) formData.append('ClientItemId', data.clientItemId);
    if (data.warehouseId) formData.append('WarehouseId', data.warehouseId);
    if (data.groupId) formData.append('GroupId', data.groupId);
    if (data.dateInInventory) formData.append('DateInInventory', data.dateInInventory);
    if (data.currentQuantity !== undefined && data.currentQuantity !== null) {
      formData.append('CurrentQuantity', data.currentQuantity);
    }
    if (data.makat) formData.append('Makat', data.makat);
    if (data.name) formData.append('Name', data.name);
    if (data.price !== undefined && data.price !== null) formData.append('Price', data.price);
    if (data.includeVat !== undefined && data.includeVat !== null) {
      formData.append('IncludeVat', data.includeVat);
    }
    
    // Handle image: only send if there's a new file
    // If no new file, backend will automatically preserve the existing S3 key
    if (data.imageFile) {
      formData.append('ImageFile', data.imageFile);
    }

    const response = await fetch(`${API_URL}/Inventory/UpdateInventoryItemWithClientItem`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        // Don't set Content-Type - let browser set it with boundary for multipart/form-data
      },
      body: formData,
    });
    return await handleResponse(response, "Failed to update inventory item");
  } catch (err) {
    console.error("Error in UpdateInventoryItemWithClientItem:", err);
    throw err;
  }
};



const DeleteInventoryMovement = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/DeleteInventoryMovement`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to delete inventory movement");
  } catch (err) {
    console.error("Error in DeleteInventoryMovement:", err);
    throw err;
  }
};

const GetInventoryMovementById = async (caseId, id, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetInventoryMovementById`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, Id: id }),
    });
    return await handleResponse(response, "Failed to fetch inventory movement by ID");
  } catch (err) {
    console.error("Error in GetInventoryMovementById:", err);
    throw err;
  }
};

const GetInventoryMovementsByCaseId = async (caseId, authToken) => {
  try {
    const response = await fetch(`${API_URL}/Inventory/GetInventoryMovementsByCaseId`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId }),
    });
    return await handleResponse(response, "Failed to fetch inventory movements by case ID");
  } catch (err) {
    console.error("Error in GetInventoryMovementsByCaseId:", err);
    throw err;
  }
};

const GetMovementsByInventoryItem = async (payload, authToken) => {
  try {
    const { inventoryItemId, caseId } = payload;
    const response = await fetch(`${API_URL}/Inventory/GetMovementsByInventoryItem`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ CaseId: caseId, InventoryItemId: inventoryItemId }),
    });
    return response;
  } catch (err) {
    console.error("Error in GetMovementsByInventoryItem:", err);
    throw err;
  }
};

/*---------------------------------------------------
  OCR IMPORT
----------------------------------------------------*/
const CreateInventoryByOCR = async (
  caseId,
  files /* File[] */,
  groupingMode = Constants.InvoiceGroupingMode.MergeAll,
  authToken,
) => {
  try {
    const fd = new FormData();
    fd.append("CaseId", caseId);
    files.forEach((f) => fd.append("Documents", f));
    // Always append GroupingMode (even if 0, which is valid for MergeAll)
    // Convert to string explicitly to ensure proper transmission
    fd.append("GroupingMode", String(groupingMode ?? Constants.InvoiceGroupingMode.MergeAll));
    fd.append("StreamUpload", "true");

    const response = await fetch(`${API_URL}/Inventory/CreateByOCR`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: fd,
    });
    return response;
  } catch (err) {
    console.error("Error in CreateInventoryByOCR:", err);
    throw err;
  }
};


/* src/services/inventory.service.js
   --------------------------------- */
const CreateMultipleInventoryMovements = async (payload, authToken) => {
  try {
    
    const fd = new FormData();

    /* ─── scalars ─────────────────────────────── */
    fd.append("CaseId",            payload.caseId);
    fd.append("UseSharedDocument", payload.useSharedDocument ? "true" : "false");

    if (payload.documentType !== undefined && payload.documentType !== null) {
      fd.append("DocumentType", payload.documentType);   // backend parses enum
    }

    /* ─── shared document (optional) ───────────── */
    if (payload.document instanceof File) {
      fd.append("Document", payload.document);
    }

    if (payload.documents && Array.isArray(payload.documents)) {
      payload.documents.forEach((doc) => {
        if (doc instanceof File) {
          fd.append("Documents", doc);
        }
      });
    }

    /* ─── original uploaded file URLs (for MergeAll mode) ───────────── */
    if (payload.originalUploadedFileUrls && Array.isArray(payload.originalUploadedFileUrls) && payload.originalUploadedFileUrls.length > 0) {
      fd.append("OriginalUploadedFileUrls", JSON.stringify(payload.originalUploadedFileUrls));
    }

    if (payload.priceIncludingVAT !== undefined && payload.priceIncludingVAT !== null) {
      fd.append("PriceIncludingVAT", payload.priceIncludingVAT ? "true" : "false");
    }

    if (payload.hideAmount !== undefined && payload.hideAmount !== null) {
      fd.append("HideAmount", payload.hideAmount ? "true" : "false");
    }

    if (payload.signature) {
      fd.append("Signature", payload.signature);
    }

    if (payload.generatedBy) {
      fd.append("GeneratedBy", payload.generatedBy);
    }

    if (payload.clientName) fd.append("ClientName", payload.clientName);
    if (payload.clientCaseId) fd.append("ClientCaseId", payload.clientCaseId);
    if (payload.clientPhoneNumber) fd.append("ClientPhoneNumber", payload.clientPhoneNumber);
    if (payload.clientEmail) fd.append("ClientEmail", payload.clientEmail);
    if (payload.clientLocation) fd.append("ClientLocation", payload.clientLocation);
    if (payload.deliveryLocation) fd.append("DeliveryLocation", payload.deliveryLocation);

    /* ─── movements array  ➜ plain *string* value */
    /*     The model-binder treats each              *
     *     form["Movements"] value as raw JSON text. */
      const movementsWithCaseId = (payload.movements || []).map(m => ({
      ...m,
      caseId: payload.caseId,     
      documentType: payload.documentType,      // ← inject once here
    }));

    fd.append("Movements", JSON.stringify(movementsWithCaseId));

    const resp = await fetch(
      `${API_URL}/Inventory/CreateMultipleInventoryMovements`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` }, // NO Content-Type
        body: fd,
      }
    );
    const jsonData = await resp.json();
    return jsonData;
  } catch (err) {
    console.error("Error in CreateMultipleInventoryMovements:", err);
    throw err;
  }
};

/*---------------------------------------------------
  Generate draft PDF for multiple inventory movements
  --------------------------------------------------*/
const CreateMultipleInventoryMovementsDraftPdf = async (payload, authToken) => {
  try {
    const response = await fetch(
      `${API_URL}/Inventory/CreateMultipleInventoryMovementsDraftPdf`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    // Parse JSON response containing array of base64 PDFs
    const data = await response.json();
    
    // Convert base64 strings to Blobs
    const pdfBlobs = data.pdfs.map(base64String => {
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'application/pdf' });
    });
    
    return pdfBlobs;
  } catch (err) {
    console.error("Error in CreateMultipleInventoryMovementsDraftPdf:", err);
    throw err;
  }
};

/*---------------------------------------------------
  lock / unlock a single inventory movement
  --------------------------------------------------*/
const LockInventoryMovement = async (payload, authToken) => {
  /*  payload shape:
      {
        caseId:  string,          // GUID
        id:      string,          // movement GUID
        isLocked: boolean         // true → lock, false → unlock
      }
  */
  try {
    const response = await fetch(`${API_URL}/Inventory/LockInventoryMovement`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      // convert to the Pascal-case keys your .NET DTO expects
      body: JSON.stringify({
        CaseId:  payload.caseId,
        Id:      payload.id,
        isLocked: payload.isLocked,
      }),
    });

    return await handleResponse(
      response,
      "Failed to lock / unlock inventory movement"
    );
  } catch (err) {
    console.error("Error in LockInventoryMovement:", err);
    throw err;
  }
};

/*---------------------------------------------------
  export
----------------------------------------------------*/
const InventoryService = {
  // Items
  CreateInventoryItem,
  CreateMultipleInventoryItems,
  UpdateInventoryItem,
  DeleteInventoryItem,
  MergeInventoryItems,
  GetInventoryItemById,
  GetInventoryItemsByCaseId,
  GetInventoryItemsWithClientItemByCaseId,
  GetInventoryItemsWithClientItemAndMovementsByCaseId,
  // Groups
  CreateInventoryGroup,
  UpdateInventoryGroup,
  DeleteInventoryGroup,
  GetInventoryGroupById,
  GetInventoryGroupsByCaseId,
  // Warehouses
  CreateWarehouse,
  UpdateWarehouse,
  DeleteWarehouse,
  GetWarehouseById,
  GetWarehousesByCaseId,
  // Movements
  CreateInventoryMovement,
  CreateMultipleInventoryMovements,
  CreateMultipleInventoryMovementsDraftPdf,
  UpdateInventoryMovement,
  DeleteInventoryMovement,
  LockInventoryMovement, 
  GetInventoryMovementById,
  GetInventoryMovementsByCaseId,
  GetMovementsByInventoryItem,
  // OCR
  CreateInventoryByOCR,
  UpdateInventoryItemWithClientItem
};

export default InventoryService;